/**
 * Layout dedicato alla UI mobile del terzista (prefisso URL /m/*).
 *
 * - Header minimale con logo, titolo pagina e menu profilo
 * - Nessuna sidebar (navigation via bottom tab o azioni in-page)
 * - Area content a tutta larghezza, padding piccolo
 */
import { Avatar, Button, Dropdown, Layout, Modal, Form, Input, message, Tooltip } from 'antd'
import { LogoutOutlined, LockOutlined, UserOutlined, ArrowLeftOutlined } from '@ant-design/icons'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { authApi } from '@/api/auth'
import client from '@/api/client'
import { useAuthStore } from '@/store/authStore'
import ErrorBoundary from '@/components/ErrorBoundary'
import { useSyncStatus } from '@/hooks/useSyncStatus'
import { drainPendingQueue } from '@/lib/offlineQueue'

function SyncBadge() {
  const { online, pendingCount } = useSyncStatus()
  const color = !online ? '#ff4d4f' : pendingCount > 0 ? '#faad14' : '#52c41a'
  const tooltip = !online
    ? 'Offline — i dati saranno sincronizzati quando torna la connessione'
    : pendingCount > 0
      ? `${pendingCount} upload in attesa di sincronizzazione`
      : 'Sincronizzato'
  return (
    <Tooltip title={tooltip}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 10px',
          borderRadius: 12,
          background: 'rgba(255,255,255,0.12)',
        }}
      >
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: color,
            boxShadow: `0 0 6px ${color}`,
          }}
        />
        {pendingCount > 0 && online && (
          <span style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>{pendingCount}</span>
        )}
      </div>
    </Tooltip>
  )
}

function GpsBadge() {
  const [status, setStatus] = useState<string>(() => localStorage.getItem('edr.gps.status') || 'unknown')
  const [debug, setDebug] = useState<string>(() => localStorage.getItem('edr.gps.debug') || '')

  useEffect(() => {
    const id = setInterval(() => {
      setStatus(localStorage.getItem('edr.gps.status') || 'unknown')
      setDebug(localStorage.getItem('edr.gps.debug') || '')
    }, 2000)
    return () => clearInterval(id)
  }, [])

  const isMobileApp = typeof window !== 'undefined' &&
    (window as unknown as { __EDR_IS_MOBILE_APP__?: boolean }).__EDR_IS_MOBILE_APP__
  if (!isMobileApp) return null

  const color = status === 'on' ? '#52c41a' : status === 'off' ? '#ff4d4f' : '#faad14'
  const label = status === 'on' ? 'GPS' : status === 'off' ? 'GPS KO' : 'GPS ?'
  return (
    <Tooltip title={debug || 'Stato GPS sconosciuto'}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 10px',
          borderRadius: 12,
          background: 'rgba(255,255,255,0.12)',
          cursor: 'help',
        }}
      >
        <div
          style={{
            width: 8, height: 8, borderRadius: '50%',
            background: color, boxShadow: `0 0 6px ${color}`,
          }}
        />
        <span style={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>{label}</span>
      </div>
    </Tooltip>
  )
}

const { Header, Content } = Layout

export default function MobileLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { utente, clearAuth } = useAuthStore()
  const [pwdOpen, setPwdOpen] = useState(false)
  const [pwdForm] = Form.useForm()

  // Drain della coda offline quando torna la connessione
  useEffect(() => {
    const handler = () => { void drainPendingQueue() }
    window.addEventListener('online', handler)
    // Tenta drenaggio anche al mount
    if (typeof navigator !== 'undefined' && navigator.onLine) {
      void drainPendingQueue()
    }
    return () => window.removeEventListener('online', handler)
  }, [])

  const handleLogout = async () => {
    // Ferma GPS background se attivo (no-op se non in app mobile)
    const w = window as unknown as {
      __EDR_IS_MOBILE_APP__?: boolean
      EDRNative?: { call: (fn: string, args?: unknown) => void }
    }
    if (w.__EDR_IS_MOBILE_APP__ && w.EDRNative) {
      w.EDRNative.call('stopGpsTracking', {})
    }
    await authApi.logout()
    clearAuth()
    navigate('/login', { replace: true })
  }

  const cambiaPwd = useMutation({
    mutationFn: (v: { password_attuale: string; nuova_password: string }) =>
      client.patch('/utenti/me/password', v),
    onSuccess: () => {
      message.success('Password aggiornata')
      setPwdOpen(false)
      pwdForm.resetFields()
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Errore cambio password'),
  })

  // "Back" mostrato se non siamo sulla root della sezione mobile
  const canGoBack = location.pathname !== '/m/interventi' && location.pathname !== '/m'

  return (
    <Layout style={{ minHeight: '100vh', background: '#f5f7fa' }}>
      <Header
        style={{
          background: 'linear-gradient(90deg, #0a1628 0%, #1a3a6b 100%)',
          padding: '0 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 56,
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {canGoBack ? (
            <Button
              type="text"
              icon={<ArrowLeftOutlined style={{ color: '#fff', fontSize: 18 }} />}
              onClick={() => navigate(-1)}
            />
          ) : (
            <img src="/logo.png" alt="EDR" style={{ height: 32, objectFit: 'contain' }} />
          )}
          <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>EDR Mobile</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <GpsBadge />
          <SyncBadge />
          <Dropdown
          menu={{
            items: [
              { key: 'pwd', icon: <LockOutlined />, label: 'Cambia password', onClick: () => { pwdForm.resetFields(); setPwdOpen(true) } },
              { type: 'divider' },
              { key: 'logout', icon: <LogoutOutlined />, label: 'Esci', danger: true, onClick: handleLogout },
            ],
          }}
          trigger={['click']}
          placement="bottomRight"
        >
          <Avatar style={{ background: '#2a5a9b', cursor: 'pointer' }} icon={<UserOutlined />}>
            {utente?.nome?.[0]?.toUpperCase()}
          </Avatar>
        </Dropdown>
        </div>
      </Header>

      <Content style={{ padding: 12 }}>
        <ErrorBoundary key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </Content>

      <Modal
        title="Cambia password"
        open={pwdOpen}
        onCancel={() => setPwdOpen(false)}
        onOk={() => pwdForm.submit()}
        confirmLoading={cambiaPwd.isPending}
        okText="Salva"
        cancelText="Annulla"
        width="95%"
        style={{ maxWidth: 480 }}
      >
        <Form
          form={pwdForm}
          layout="vertical"
          style={{ marginTop: 12 }}
          onFinish={(v) => cambiaPwd.mutate(v)}
        >
          <Form.Item name="password_attuale" label="Password attuale" rules={[{ required: true }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="nuova_password"
            label="Nuova password"
            rules={[{
              validator: (_, v) => {
                if (!v) return Promise.reject('Obbligatoria')
                if (v.length < 8) return Promise.reject('Minimo 8 caratteri')
                if (!/[a-zA-Z]/.test(v) || !/[0-9]/.test(v))
                  return Promise.reject('Almeno una lettera e un numero')
                return Promise.resolve()
              },
            }]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  )
}
